/**
 * Client entrypoint for file intake. Reads a File's bytes and parses them in a
 * Web Worker when the environment supports one (so a 60MB xlsx never freezes
 * the tab — criterion 10); falls back to a direct parse on the current thread
 * when Worker is unavailable (SSR, tests, older runtimes). Returns the parsed
 * grid; parse errors surface as thrown Errors with actionable messages.
 */

import { detectFileKind, parseBuffer, type ParseGrid } from "./parse";
import type { Delimiter } from "./sniff";
import type { ParseWorkerRequest, ParseWorkerResponse } from "./parse.worker";

let seq = 0;

/** True when this runtime can offload parsing to a Web Worker. */
export function workerSupported(): boolean {
  return typeof Worker !== "undefined";
}

async function parseInWorker(kind: ReturnType<typeof detectFileKind>, bytes: ArrayBuffer, delimiter?: Delimiter): Promise<ParseGrid> {
  const worker = new Worker(new URL("./parse.worker.ts", import.meta.url), { type: "module" });
  const id = ++seq;
  try {
    return await new Promise<ParseGrid>((resolve, reject) => {
      worker.onmessage = (e: MessageEvent<ParseWorkerResponse>) => {
        if (e.data.id !== id) return;
        if (e.data.ok) resolve(e.data.grid);
        else reject(new Error(e.data.error));
      };
      worker.onerror = () => reject(new Error("The file could not be parsed. Try exporting it as CSV."));
      const req: ParseWorkerRequest = { id, kind, bytes, delimiter };
      worker.postMessage(req, [bytes]); // transfer, no copy
    });
  } finally {
    worker.terminate();
  }
}

export async function parseFile(file: File, delimiter?: Delimiter): Promise<ParseGrid> {
  const kind = detectFileKind(file.name, file.type);
  const bytes = await file.arrayBuffer();
  if (workerSupported()) return parseInWorker(kind, bytes, delimiter);
  return parseBuffer(kind, new Uint8Array(bytes), delimiter);
}
